import axios from 'axios';
import ApiService from './ApiService';

/**
 * Azure Storage Service for handling XML file operations
 */
class AzureStorageService {
  constructor() {
    this.accountName = null;
    this.containerName = 'fracfocus-xml-files';
    this.accountKey = null;
    this.backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    
    this.initialize();
  }

  /**
   * Initialize the Azure Blob Storage configuration
   */
  async initialize() {
    try {
      // Get Azure Storage configuration from the backend
      const config = await ApiService.getAzureStorageConfig();
      
      if (!config) {
        console.warn('Azure Storage configuration is not available');
        return;
      }
      
      // Check if we have the necessary configuration
      if (!config.accountName || !config.accountKey) {
        console.warn('Azure Storage account name or account key is not configured');
        return;
      }
      
      this.accountName = config.accountName;
      this.accountKey = config.accountKey;
      this.containerName = config.containerName || 'fracfocus-xml-files';
      
      console.log('Azure Storage configuration loaded successfully');
    } catch (error) {
      console.error('Error initializing Azure Storage:', error);
    }
  }

  /**
   * Save an XML file to Azure Blob Storage
   * @param {string} fileName - The name of the file
   * @param {string} xmlContent - The XML content to save
   * @param {Object} metadata - Optional metadata for the file
   * @returns {Promise<Object>} - The result of the operation
   */
  async saveXmlFile(fileName, xmlContent, metadata = {}) {
    if (!this.accountName || !this.accountKey) {
      // Try to initialize again
      await this.initialize();
      
      if (!this.accountName || !this.accountKey) {
        throw new Error('Azure Storage is not initialized');
      }
    }

    try {
      // Get the token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token is missing');
      }
      
      // Add upload date to metadata
      const metadataWithDate = {
        ...metadata,
        uploadDate: new Date().toISOString()
      };
      
      // Use the backend API to upload the file
      const response = await axios.post(
        `${this.backendUrl}/api/azure-storage/upload`,
        {
          fileName,
          content: xmlContent,
          metadata: metadataWithDate
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.success) {
        return {
          success: true,
          fileName,
          url: response.data.result.url,
          timestamp: response.data.result.timestamp
        };
      } else {
        throw new Error(response.data.error || 'Failed to upload file');
      }
    } catch (error) {
      console.error('Error saving XML file to Azure Storage:', error);
      throw error;
    }
  }

  /**
   * Get a list of all XML files in the container that the current user has access to
   * @returns {Promise<Array>} - Array of file objects
   */
  async listXmlFiles() {
    if (!this.accountName || !this.accountKey) {
      // Try to initialize again
      await this.initialize();
      
      if (!this.accountName || !this.accountKey) {
        throw new Error('Azure Storage is not initialized');
      }
    }

    try {
      // Get the token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token is missing');
      }
      
      // Get the current user's accessible customers
      const accessibleCustomers = await this.getUserAccessibleCustomers();
      
      // Check if user is assigned to ProFrac (company id = 133)
      const isProFracUser = await this.isUserAssignedToProFrac();
      
      // Use the backend API to list files
      const response = await axios.get(
        `${this.backendUrl}/api/azure-storage/list`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.data) {
        return [];
      }
      
      // Filter files based on customer access
      // ProFrac users (company id = 133) can view all XMLs
      const files = response.data.filter(blob => {
        if (isProFracUser) {
          return true; // ProFrac users can view all XMLs
        }
        
        const fileCustomerId = blob.metadata?.customerId;
        return !fileCustomerId || accessibleCustomers.includes(fileCustomerId);
      }).map(blob => ({
        id: blob.name,
        name: blob.name,
        url: blob.url,
        date: blob.last_modified,
        size: blob.size,
        metadata: blob.metadata,
        wellName: blob.metadata?.wellName || 'Unknown Well',
        operator: blob.metadata?.operator || 'Unknown Operator',
        customerId: blob.metadata?.customerId || null,
        customerName: blob.metadata?.customerName || null
      }));
      
      return files;
    } catch (error) {
      console.error('Error listing XML files from Azure Storage:', error);
      throw error;
    }
  }
  
  /**
   * Get the list of customer IDs that the current user has access to
   * @returns {Promise<Array>} - Array of customer IDs
   */
  async getUserAccessibleCustomers() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return [];
      }
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const response = await axios.get(`${backendUrl}/api/users/me/customers`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && Array.isArray(response.data)) {
        // Extract customer IDs from the response
        return response.data.map(customer => customer.id.toString());
      }
      
      return [];
    } catch (error) {
      console.error('Error getting user accessible customers:', error);
      return [];
    }
  }

  /**
   * Check if the current user is assigned to ProFrac (company id = 133)
   * @returns {Promise<boolean>} - True if the user is assigned to ProFrac
   */
  async isUserAssignedToProFrac() {
    try {
      const accessibleCustomers = await this.getUserAccessibleCustomers();
      // Check if the user has access to ProFrac (company id = 133)
      return accessibleCustomers.includes('133');
    } catch (error) {
      console.error('Error checking ProFrac assignment:', error);
      return false;
    }
  }

  /**
   * Check if the current user has access to a specific customer
   * @param {string} customerId - The customer ID to check
   * @returns {Promise<boolean>} - True if the user has access to the customer
   */
  async hasAccessToCustomer(customerId) {
    if (!customerId) return true; // If no customer ID is provided, assume access is allowed
    
    // Check if user is assigned to ProFrac (company id = 133) - they can access all files
    const isProFracUser = await this.isUserAssignedToProFrac();
    if (isProFracUser) {
      return true;
    }
    
    const accessibleCustomers = await this.getUserAccessibleCustomers();
    return accessibleCustomers.includes(customerId.toString());
  }

  /**
   * Get the content of an XML file
   * @param {string} fileName - The name of the file
   * @returns {Promise<string>} - The XML content
   */
  async getXmlFileContent(fileName) {
    if (!this.accountName || !this.accountKey) {
      // Try to initialize again
      await this.initialize();
      
      if (!this.accountName || !this.accountKey) {
        throw new Error('Azure Storage is not initialized');
      }
    }

    try {
      // Get the token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token is missing');
      }
      
      // First, get the file metadata to check customer access
      const files = await this.listXmlFiles();
      const fileInfo = files.find(file => file.name === fileName);
      
      if (!fileInfo) {
        throw new Error('File not found');
      }
      
      // Check if the user has access to this file's customer
      if (fileInfo.metadata?.customerId) {
        const hasAccess = await this.hasAccessToCustomer(fileInfo.metadata.customerId);
        if (!hasAccess) {
          throw new Error('You do not have access to this file');
        }
      }
      
      // Use the backend API to download the file
      const response = await axios.get(
        `${this.backendUrl}/api/azure-storage/download/${encodeURIComponent(fileName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/xml'
          },
          responseType: 'text'
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error getting XML file content from Azure Storage:', error);
      throw error;
    }
  }

  /**
   * Delete an XML file from Azure Blob Storage
   * @param {string} fileName - The name of the file
   * @returns {Promise<boolean>} - True if the file was deleted
   */
  async deleteXmlFile(fileName) {
    if (!this.accountName || !this.accountKey) {
      // Try to initialize again
      await this.initialize();
      
      if (!this.accountName || !this.accountKey) {
        throw new Error('Azure Storage is not initialized');
      }
    }

    try {
      // Get the token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token is missing');
      }
      
      // First, get the file metadata to check customer access
      const files = await this.listXmlFiles();
      const fileInfo = files.find(file => file.name === fileName);
      
      if (!fileInfo) {
        throw new Error('File not found');
      }
      
      // Check if the user has access to this file's customer
      if (fileInfo.metadata?.customerId) {
        const hasAccess = await this.hasAccessToCustomer(fileInfo.metadata.customerId);
        if (!hasAccess) {
          throw new Error('You do not have access to this file');
        }
      }
      
      // Use the backend API to delete the file
      const response = await axios.delete(
        `${this.backendUrl}/api/azure-storage/delete/${encodeURIComponent(fileName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.success) {
        return true;
      } else {
        throw new Error(response.data.error || 'Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting XML file from Azure Storage:', error);
      throw error;
    }
  }

}

// Create and export a singleton instance
const azureStorageService = new AzureStorageService();
export default azureStorageService;
